/// <reference types="vite/client" />

import _ from "lodash"
import { fi } from "date-fns/locale"
import i18n from "./i18n"
import type { TFunction } from "i18next"

export const VERSION: string = import.meta.env.VITE_VERSION || "1.2.0beta"
export const MULTILANGUAGE_SUPPORT_ENABLED: boolean = true
const USE_SAME_DOMAIN: boolean = !!import.meta.env.VITE_USE_SAME_DOMAIN && import.meta.env.VITE_USE_SAME_DOMAIN !== "false"
const BACKEND_PROXY_PATH: string = import.meta.env.VITE_BACKEND_PROXY_PATH
const domain: string = window.location.protocol + "//" + window.location.hostname + ":3000"
export const NEW_ID: string = "_new"
export const BACKEND_URL: string = USE_SAME_DOMAIN
	? domain
	: import.meta.env.VITE_ENABLE_VITE_BASED_BACKEND_PROXY === "true" ||
		  import.meta.env.VITE_ENABLE_NGINX_BASED_BACKEND_PROXY === "true"
		? `${window.location.origin}${BACKEND_PROXY_PATH}`
		: import.meta.env.VITE_BACKEND_URL
export const BACKEND_WEBSOCKET_URL: string | undefined = import.meta.env.VITE_BACKEND_WEBSOCKET_URL
export const IMAGE_URL: string = USE_SAME_DOMAIN
	? domain + "/uploads/images"
	: import.meta.env.VITE_IMAGE_URL || BACKEND_URL + "/uploads/images"
export const SHOW_DEV_ONLY_COMPONENTS: boolean =
	!!import.meta.env.VITE_SHOW_DEV_ONLY_COMPONENTS && import.meta.env.VITE_SHOW_DEV_ONLY_COMPONENTS !== "false"
export const SHOW_DEV_DISCLAIMER: boolean = !!import.meta.env.VITE_SHOW_DEV_DISCLAIMER
export const MD_URL: string = BACKEND_URL + "/uploads/md"
export const MD_URL_EN: string = BACKEND_URL + "/en/uploads/md"
export const PDF_URL: string = BACKEND_URL + "/uploads/pdf"
export const LOG_APPLY_MEMO: boolean = !!import.meta.env.VITE_LOG_APPLY_MEMO

export const ROUTER_BASE_PATH: string = import.meta.env.VITE_ROUTER_BASE_PATH ?? ""

// Static timezone used for date display and entry instead of device-dependent locale
export const TZ: string = "Europe/Helsinki"
export const MAINTENANCE_MESSAGE: string | undefined = import.meta.env.VITE_MAINTENANCE_MESSAGE
export const MAINTENANCE_BANNER_VARIANT: string | undefined = import.meta.env.VITE_MAINTENANCE_BANNER_VARIANT
export const MAINTENANCE_USE_FILE: boolean = !!import.meta.env.VITE_MAINTENANCE_USE_FILE

export const IS_DEV: boolean =
	import.meta.env.VITE_ENVIRONMENT !== "production" &&
	(import.meta.env.VITE_ENVIRONMENT === "development" || import.meta.env.MODE === "development")
const IS_PROD = !IS_DEV
if (IS_DEV) console.log("Running development build")
if (IS_PROD) console.log("Running production build")

// Sentry options
export const ENABLE_SENTRY: boolean = import.meta.env.VITE_ENABLE_SENTRY === "true"
export const SENTRY_DSN: string = import.meta.env.VITE_SENTRY_DSN ?? ""
export const SENTRY_ENVIRONMENT: string = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? (IS_DEV ? "development" : "production")

export const RATE_LIMIT_BYPASS_KEY: string = import.meta.env.VITE_RATE_LIMIT_BYPASS_KEY ?? ""

// Customized finnish locale object that uses colon in time values
const localeCopy = _.cloneDeep(fi)
const timeFormats: { [key: string]: string } = {
	full: "HH:mm:ss zzzz",
	long: "HH:mm:ss z",
	medium: "HH:mm:ss",
	short: "HH:mm",
}
_.set(localeCopy, "formatLong.time", (opts: { width?: "full" | "long" | "medium" | "short" }) => {
	return timeFormats[opts?.width ?? "full"]
})
export const getTranslations = (t?: TFunction) => {
	if (!t) {
		t = i18n.t
	}
	return {
		antropometry: t("Antropometria"),
		physical: t("Fyysinen"),
		sport_skill: t("Lajitekniikka"),
		knowledge: t("Tieto"),
		general: t("Yleinen"),
		muscle_balance: t("Lihastasapaino ja liikkuvuus"),
		football: t("Jalkapallo"),
		floorball: t("Salibandy"),
		team_match: t("Ottelu"),
		team_training: t("Harjoitus"),
		group_test: t("Testi"),
		team_tournament: t("Turnaus"),
		team_other: t("Muu tapahtuma"),
		team_camp: t("Leiri"),
		team_target: t("Tavoite"),
		team_period_target: t("Jaksotavoite"),
		team_year_target: t("Vuositavoite"),
		player: t("Pelaaja"),
		basic_user: t("Peruskäyttäjä"),
		coach: t("Valmentaja"),
		headcoach: t("Valmennuspäällikkö"),
		parent: t("Huoltaja"),
		admin: t("Ylläpitäjä"),
		patron: t("Pääkäyttäjä"),
	}
}
export const EVENT_TYPE_COLORS: { [key: string]: string } = {
	default: "#0031CA",
	team_match: "#00514C",
	team_training: "#5888E2",
	group_test: "#FFCE84",
	team_tournament: "#001857",
	team_other: "#5E749E",
	other: "#5E749E",
	team_camp: "#F2F2F2",
	team_target: "#FFCE84",
	team_year_target: "#FFCE84",
	team_period_target: "#FFCE84",
	training: "#5888E2",
	absent: "#A585B5",
	injury: "#001857",
	sleep: "#33F7EB",
	survey: "#DE9DFF",
	meeting: "#117733",
	nutrition: "#0031CA",
	fba_spring: "#612E7A",
	fba_autumn: "#AD0AFF",
	fba_tournament: "#D37EFF",
	fba_international: "#A585B5",
	sha_spring: "#612E7A",
	sha_autumn: "#AD0AFF",
	sha_tournament: "#D37EFF",
	sha_international: "#A585B5",
	floorball_national: "#612E7A",
	football_national: "#AD0AFF",
	floorball_player_path: "#D37EFF",
	spl_regional_spring: "#612E7A",
	spl_regional_autumn: "#AD0AFF",
}

export const EXCEL_INSTRUCTIONS: { [key: string]: string } = {
	DATA_ENTRY_TITLE_EN: "Filling out test results",
	DATA_ENTRY_EN: `
- Test results are entered in the 'Results' sheet, selected from below tabs. 'Extra information' tab allows editing test-specific values. Other sheets do not need to be edited.
- Extra rows can be added as long as the identifying information matches with an existing user. New rows create new event participants when uploaded to the test result page.
  * Note that entering decimal numbers requires using the decimal separator defined in Excel settings. Using a wrong separator may cause the value to be autofilled as a date.
- PalloID / SporttiID is optional, but it's recommended; specify this when there are non-unique first and last names among the test participants.
- Out-of-bounds values are highlighted in red and correct values are highlighted in green.
- When result entry is finished, save the file with the .xlsx extension. Microsoft Excel does this by default.
    `,
	DATA_ENTRY_TITLE_FI: "Testitulosten täyttäminen",
	DATA_ENTRY_FI: `
- Testitulokset täytetään 'Tulokset' välilehdessä, jonka voi valita ikkunan alaosassa. 'Lisämääreet' välilehdessä voi muuttaa muita testikohtaisia tietoja. Muihin välilehtiin ei tarvitse tehdä muutoksia.
  * Huomioi että desimaaliarvoja syöttäessä on käytettävä Excel:in asetuksissa määritettyä desimaalierotinta. Väärällä merkillä arvo voi automaattisesti muuttua päivämääräksi.
- Ylimääräisiä rivejä voi lisätä, kunhan uuden rivin tunnistetiedot osuvat olemassaolevaan käyttäjään. Uudet rivit luovat lisäosallistujia tapahtumaan kun tiedosto ladataan testitulossivustolle.
- PalloID / SporttiID kenttä ei ole pakollinen, mutta on suositeltava; täytä se mikäli testin osallistujilla on sama etu- ja sukunimi.
- Minimi- ja maksimirajat ylittävät arvot korostuvat punaisella automaattisesti, oikeat arvot korostuvat vihreällä.
- Kun tulokset ovat valmiita, tallenna tiedosto .xlsx tiedostopäättellä. Microsoft Excel tekee tämän oletuksena.
    `,
}

/**
 * Colors sourced from
 *
 * https://personal.sron.nl/~pault/
 */

// Muted qualitative colour scheme
export const LINE_CHART_COLORS: string[] = [
	"#332288",
	"#88ccee",
	"#44aa99",
	"#117733",
	"#999933",
	"#ddcc77",
	"#cc6677",
	"#882255",
	"#aa4499",
]

// Bright qualitative colour scheme
export const LINE_CHART_COLORS_ALT: string[] = ["#4477aa", "#66ccee", "#228833", "#ccbb44", "#ee6677", "#aa3377"]

export const GRAPH_CHANGE_COLORS: string[] = ["#002365", "#00369a", "#0047cb", "#11d8d1", "#cb87ff", "#ae46ff", "#fab84a"]

export const STACKED_AREA_CHART_COLORS: string[] = [
	"#0348c9",
	"#13d8d1",
	"#deb5f9",
	"#004ac9",
	"#0ddad6",
	"#d28aff",
	"#026061",
	"#f9b262",
	"#b244fb",
]

export const getLocAggregates = (t?: TFunction) => {
	if (!t) {
		t = i18n.t
	}
	return {
		average: t("Keskiarvo"),
		best: t("Paras tulos"),
		maximum: t("Maksimi"),
		minimum: t("Minimi"),
		median: t("Mediaani"),
	}
}

export const EVENTS_WITH_SURFACE_TYPE: string[] = ["training", "team_match", "team_training", "team_other"]

export const ACTIONABLE_BULLETIN_TYPES: string[] = [
	"invite",
	"invite-child-when-non-parent",
	"invite-own-child",
	"invite-other-child",
	"invite-register-other-child",
	"invite-register-child-when-non-parent",
	"activate-child",
	"add_new_parent",
	"confirm-invite",
	"confirm-invite-child",
	"membership-removal-request",
]

export const UNDEFINED_STRING: string = "-"

export const ADULT_ONLY_GROUP_ROLES: string[] = ["headcoach"]

export const MEMBER_LIMIT_PRESET: number[] = [50, 150, 400, 1000, 2000]

// Basic role order by priority
export const ROLE_ORDER: string[] = [
	"admin",
	"headcoach",
	"coach",
	"hyvinvointi_valmentaja",
	"manager",
	"player",
	"hyvinvointi_osallistuja",
	"none",
]

export const getRankDescriptionsLoc = (t?: TFunction): { [key: number]: string } => {
	if (!t) {
		t = i18n.t
	}

	return {
		5: t("ominaisuus on kansainvälisellä kärkitasolla"),
		4: t("ominaisuus on kansallisella kärkitasolla"),
		3: t("ominaisuus on hieman yli kansallisen keskitason"),
		2: t("ominaisuus on hieman alle kansallisen keskitason"),
		1: t("ominaisuus on alle kansallisen keskitason"),
	}
}

export const getRankDescriptions1To3Loc = (t?: TFunction): { [key: number]: string } => {
	if (!t) {
		t = i18n.t
	}

	return {
		3: t("ominaisuus on kansainvälisellä kärkitasolla"),
		2: t("ominaisuus on kansallisella keskitasolla"),
		1: t("ominaisuus on alle kansallisen keskitason"),
	}
}

export const PLAYER_BIRTH_MONTH_STATIC_CRITERIA_MALE: { [key: number]: number } = {
	0: 14.97,
	1: 14.88,
	2: 14.8,
	3: 14.72,
	4: 14.63,
	5: 14.55,
	6: 14.47,
	7: 14.38,
	8: 14.3,
	9: 14.22,
	10: 14.13,
	11: 14.05,
}

export const PLAYER_BIRTH_MONTH_STATIC_CRITERIA_FEMALE: { [key: number]: number } = {
	0: 13.07,
	1: 12.98,
	2: 12.9,
	3: 12.82,
	4: 12.73,
	5: 12.65,
	6: 12.57,
	7: 12.48,
	8: 12.4,
	9: 12.32,
	10: 12.23,
	11: 12.15,
}

export const API_CODE = {
	MFA_CHALLENGE_REQUIRED: "MFA_CHALLENGE_REQUIRED",
	LOGIN_SUCCESS: "LOGIN_SUCCESS",
	ACCESS_TOKEN_INVALID: "ACCESS_TOKEN_INVALID",
	ACCESS_TOKEN_EXPIRED: "ACCESS_TOKEN_EXPIRED",
	ACCESS_TOKEN_MISSING: "ACCESS_TOKEN_MISSING",
	TOO_MANY_REQUESTS: "TOO_MANY_REQUESTS",
}
